import { useState, useEffect, useRef } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Bold, Italic, Link as LinkIcon, List, Image, Video } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_URL, formatDate, slugify } from '../../lib/utils';
import { authFetch } from '../../lib/auth';
import { toast } from 'sonner';

const categories = ['Actualités', 'Événements', 'Partenariats', 'Pédagogie', 'Vie étudiante'];

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await authFetch(`${API_URL}/blogs?published_only=false`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingPost({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      cover_image: '',
      category: 'Actualités',
      tags: [],
      published: false
    });
    setIsNew(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!editingPost.title || !editingPost.excerpt || !editingPost.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const slug = editingPost.slug || slugify(editingPost.title);

    try {
      let response;
      if (isNew) {
        response = await authFetch(`${API_URL}/blogs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...editingPost, slug })
        });
      } else {
        response = await authFetch(`${API_URL}/blogs/${editingPost.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...editingPost, slug })
        });
      }

      if (response.ok) {
        toast.success(isNew ? 'Article créé' : 'Article mis à jour');
        setEditingPost(null);
        fetchPosts();
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      const response = await authFetch(`${API_URL}/blogs/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Article supprimé');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (contentRef.current) {
      setEditingPost({ ...editingPost, content: contentRef.current.innerHTML });
    }
  };

  const insertLink = () => {
    const url = prompt('Entrez l\'URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Entrez l\'URL de l\'image:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const insertVideo = () => {
    const url = prompt('Entrez l\'URL YouTube ou Vimeo:');
    if (url) {
      let embedUrl = url;
      if (url.includes('youtube.com/watch')) {
        const videoId = new URL(url).searchParams.get('v');
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
      const iframe = `<iframe width="560" height="315" src="${embedUrl}" frameborder="0" allowfullscreen></iframe>`;
      execCommand('insertHTML', iframe);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-blog">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#003366]">Blog & Actualités</h1>
            <p className="text-gray-500 mt-1">{posts.length} articles</p>
          </div>
          <Button onClick={handleNew} className="mt-4 md:mt-0 bg-[#DC143C] hover:bg-[#B01030] rounded-full" data-testid="new-post-btn">
            <Plus size={18} className="mr-2" /> Nouvel article
          </Button>
        </div>

        {/* Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <div key={post.id} className="admin-card overflow-hidden">
              {post.cover_image ? (
                <img src={post.cover_image} alt={post.title} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-[#003366] to-[#001a33]" />
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge badge-blue text-xs">{post.category}</span>
                  {!post.published && (
                    <span className="badge bg-gray-100 text-gray-600 text-xs">Brouillon</span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-[#003366] mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{formatDate(post.created_at)}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(post)} className="flex-1">
                    <Edit size={14} className="mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(post.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {posts.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              Aucun article. Cliquez sur "Nouvel article" pour commencer.
            </div>
          )}
        </div>

        {/* Editor Dialog */}
        <Dialog open={!!editingPost} onOpenChange={() => setEditingPost(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#003366]">
                {isNew ? 'Nouvel article' : 'Modifier l\'article'}
              </DialogTitle>
            </DialogHeader>
            
            {editingPost && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#003366] font-semibold">Titre *</Label>
                    <Input
                      value={editingPost.title}
                      onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                      className="mt-2"
                      placeholder="Titre de l'article"
                      data-testid="blog-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#003366] font-semibold">Slug (URL)</Label>
                    <Input
                      value={editingPost.slug}
                      onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
                      className="mt-2"
                      placeholder="titre-de-article"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#003366] font-semibold">Catégorie</Label>
                    <Select 
                      value={editingPost.category} 
                      onValueChange={(v) => setEditingPost({ ...editingPost, category: v })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#003366] font-semibold">Image de couverture (URL)</Label>
                    <Input
                      value={editingPost.cover_image || ''}
                      onChange={(e) => setEditingPost({ ...editingPost, cover_image: e.target.value })}
                      className="mt-2"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#003366] font-semibold">Résumé *</Label>
                  <Textarea
                    value={editingPost.excerpt}
                    onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                    className="mt-2"
                    placeholder="Bref résumé de l'article..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="text-[#003366] font-semibold mb-2 block">Contenu *</Label>
                  
                  {/* Rich Editor Toolbar */}
                  <div className="editor-toolbar">
                    <button type="button" onClick={() => execCommand('bold')} title="Gras">
                      <Bold size={16} />
                    </button>
                    <button type="button" onClick={() => execCommand('italic')} title="Italique">
                      <Italic size={16} />
                    </button>
                    <button type="button" onClick={() => execCommand('formatBlock', 'h2')} title="Titre H2">
                      H2
                    </button>
                    <button type="button" onClick={() => execCommand('formatBlock', 'h3')} title="Titre H3">
                      H3
                    </button>
                    <button type="button" onClick={() => execCommand('insertUnorderedList')} title="Liste">
                      <List size={16} />
                    </button>
                    <button type="button" onClick={insertLink} title="Lien">
                      <LinkIcon size={16} />
                    </button>
                    <button type="button" onClick={insertImage} title="Image">
                      <Image size={16} />
                    </button>
                    <button type="button" onClick={insertVideo} title="Vidéo">
                      <Video size={16} />
                    </button>
                  </div>

                  {/* Rich Editor Content */}
                  <div
                    ref={contentRef}
                    className="editor-content"
                    contentEditable
                    dangerouslySetInnerHTML={{ __html: editingPost.content }}
                    onInput={(e) => setEditingPost({ ...editingPost, content: e.currentTarget.innerHTML })}
                    data-testid="blog-content-editor"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingPost.published}
                      onCheckedChange={(v) => setEditingPost({ ...editingPost, published: v })}
                    />
                    <Label className="text-[#003366] font-semibold">
                      {editingPost.published ? 'Publié' : 'Brouillon'}
                    </Label>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 border-t">
                  <Button onClick={() => setEditingPost(null)} variant="outline" className="flex-1">
                    Annuler
                  </Button>
                  <Button onClick={handleSave} className="flex-1 bg-[#003366] hover:bg-[#002244]" data-testid="save-blog-btn">
                    <Save size={16} className="mr-2" /> Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
